import { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { PatientCard } from "../components/PatientCard";
import { patientApi } from "../services/api";
import { Search, Filter, X, Users, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const PAGE_SIZE = 50;

export default function PatientList() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // Contor separat pentru skip — nu depinde de patients.length (care se poate reduce prin ștergere sau eliminare din listă la toggle-citit)
  const [nextSkip, setNextSkip] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sexFilter, setSexFilter] = useState("");
  // Default: „necitit" pentru a reduce încărcarea inițială (evită crash pe baze mari)
  const [citFilter, setCitFilter] = useState("necitit");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Guard împotriva race-condițiilor între cereri concurente (filtru schimbat rapid)
  const requestSeq = useRef(0);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const buildParams = useCallback((skip) => {
    const params = { limit: PAGE_SIZE, skip };
    if (debouncedSearch) params.search = debouncedSearch;
    if (sexFilter && sexFilter !== "all") params.sex = sexFilter;
    if (citFilter && citFilter !== "all") params.citit = citFilter;
    return params;
  }, [debouncedSearch, sexFilter, citFilter]);

  // (Re)încarcă prima pagină la orice schimbare de filtru/căutare
  const loadFirstPage = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setHasMore(true);
    try {
      const data = await patientApi.getAll(buildParams(0));
      if (seq !== requestSeq.current) return; // stale response
      const chunk = data || [];
      setPatients(chunk);
      setNextSkip(chunk.length);
      setHasMore(chunk.length === PAGE_SIZE);
    } catch (error) {
      console.error("Failed to load patients:", error);
      if (seq === requestSeq.current) {
        setPatients([]);
        setNextSkip(0);
        setHasMore(false);
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const seq = requestSeq.current; // fixat la snapshot-ul curent
    try {
      const data = await patientApi.getAll(buildParams(nextSkip));
      if (seq !== requestSeq.current) return; // filtru schimbat între timp, drop
      const chunk = data || [];
      setPatients((prev) => [...prev, ...chunk]);
      setNextSkip((prev) => prev + chunk.length);
      setHasMore(chunk.length === PAGE_SIZE);
    } catch (error) {
      console.error("Failed to load more patients:", error);
    } finally {
      if (seq === requestSeq.current) setLoadingMore(false);
    }
  }, [loadingMore, hasMore, nextSkip, buildParams]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const clearFilters = () => {
    setSearchTerm("");
    setSexFilter("");
    setCitFilter("necitit"); // revine la default (necitiți) — nu la „toți"
  };

  const hasFilters = searchTerm || sexFilter || citFilter !== "necitit";

  return (
    <div data-testid="patient-list-page" className="page-container">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
          Sportivi
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestionează și vizualizează fișele sportivilor
        </p>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="search-input"
            placeholder="Caută după nume, prenume, club sportiv sau #număr..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-muted-foreground" />

          {/* Citit filter (default: necitit) */}
          <Select value={citFilter} onValueChange={setCitFilter}>
            <SelectTrigger data-testid="cit-filter" className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="necitit" data-testid="cit-filter-necitit">Necitiți</SelectItem>
              <SelectItem value="citit" data-testid="cit-filter-citit">Citiți</SelectItem>
              <SelectItem value="all" data-testid="cit-filter-all">Toți</SelectItem>
            </SelectContent>
          </Select>

          {/* Sex filter */}
          <Select value={sexFilter} onValueChange={setSexFilter}>
            <SelectTrigger data-testid="sex-filter" className="w-[140px]">
              <SelectValue placeholder="Sex: Toți" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sex: Toți</SelectItem>
              <SelectItem value="Masculin">Masculin</SelectItem>
              <SelectItem value="Feminin">Feminin</SelectItem>
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              data-testid="clear-filters"
              className="gap-1"
            >
              <X className="w-4 h-4" />
              Șterge
            </Button>
          )}
        </div>
      </div>

      {/* Results count */}
      {!loading && (
        <p className="text-sm text-muted-foreground mb-4" data-testid="results-count">
          {patients.length} sportiv{patients.length !== 1 ? "i" : ""} afișa{patients.length !== 1 ? "ți" : "t"}
          {hasMore && ` (mai există rezultate — apasă „Încarcă mai mulți")`}
          {citFilter === "necitit" && (
            <span className="ml-2 text-xs italic">
              — filtru implicit: doar necitiți (pentru a evita încărcarea întregii baze)
            </span>
          )}
        </p>
      )}

      {/* Loading State */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      )}

      {/* Patient Grid */}
      {!loading && patients.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {patients.map((patient, index) => (
              <div
                key={patient.id}
                className={`animate-fade-in stagger-${Math.min((index % 6) + 1, 5)}`}
              >
                <PatientCard
                  patient={patient}
                  onDeleted={(deletedId) => setPatients((prev) => prev.filter((p) => p.id !== deletedId))}
                  onUpdated={(updatedId, patch) => {
                    // Dacă filtrul curent nu mai include pacientul după update, îl scoatem din listă.
                    if (
                      (citFilter === "necitit" && patch.citit === true) ||
                      (citFilter === "citit" && patch.citit === false)
                    ) {
                      setPatients((prev) => prev.filter((p) => p.id !== updatedId));
                    } else {
                      setPatients((prev) => prev.map((p) => (p.id === updatedId ? { ...p, ...patch } : p)));
                    }
                  }}
                />
              </div>
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="flex justify-center mt-8">
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outline"
                data-testid="load-more-btn"
                className="gap-2"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Se încarcă…
                  </>
                ) : (
                  <>Încarcă mai mulți (+{PAGE_SIZE})</>
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!loading && patients.length === 0 && (
        <div className="empty-state py-16">
          <Users className="w-16 h-16 mb-4 opacity-50" />
          <p className="text-xl font-medium">Nu s-au găsit sportivi</p>
          <p className="text-sm mt-1">
            {hasFilters
              ? "Încearcă să ajustezi căutarea sau filtrele"
              : "Adaugă primul sportiv pentru a începe"}
          </p>
          {hasFilters ? (
            <Button
              variant="outline"
              onClick={clearFilters}
              className="mt-4"
              data-testid="empty-clear-filters"
            >
              Șterge Filtrele
            </Button>
          ) : (
            <Link to="/admin/add-patient" className="mt-4">
              <Button data-testid="empty-add-patient">Adaugă Sportiv</Button>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
